import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronsLeft, ChevronsRight, Monitor, Moon, Sun, X } from 'lucide-react';
import type { SessionUser } from '@familienbaum/shared';
import { useTheme, type ThemePreference } from '@/lib/theme';
import { Button } from '@/components/ui/button';
import { Segmented } from '@/components/ui/misc';
import { useIsWide } from '@/lib/use-media-query';
import { GlobalSearch } from './global-search';
import {
  HeaderSlotProvider,
  HideHeaderProvider,
  NavDrawerProvider,
  useHeaderSlot,
} from './header-actions';
import { UserMenu } from './user-menu';
import { SidebarBrand, SidebarNav, SidebarStats } from './sidebar';
import { MobileTabs } from './mobile-tabs';
import { cn } from '@/lib/utils';

const COLLAPSE_KEY = 'fb-sidebar-collapsed';

function ThemeToggle() {
  const { preference, setPreference } = useTheme();
  return (
    <Segmented<ThemePreference>
      label="Farbschema"
      value={preference}
      onValueChange={setPreference}
      options={[
        { value: 'light', label: <Sun aria-hidden />, title: 'Hell' },
        { value: 'dark', label: <Moon aria-hidden />, title: 'Dunkel' },
        { value: 'system', label: <Monitor aria-hidden />, title: 'System' },
      ]}
    />
  );
}

export function AppShell({ user }: { user: SessionUser }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [headerSlotRef, headerSlot] = useHeaderSlot();
  /**
   * Suchfeld oder Lupenknopf – eine Frage der Struktur, deshalb in JavaScript
   * und nicht mit `hidden lg:block`: zwei Fassungen im Baum hieße zwei
   * Eingabefelder, zwei Zustände und zwei Tastaturgriffe auf demselben
   * Kürzel.
   */
  const wide = useIsWide();
  /** Seiten mit eigenem Kopfband schalten die der App ab – siehe `useHideAppHeader`. */
  const [headerHidden, setHeaderHidden] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignorieren */
    }
  }, [collapsed]);

  // Beim Seitenwechsel die Schublade schließen und nach oben scrollen.
  useEffect(() => {
    setDrawerOpen(false);
    document.getElementById('page-scroll')?.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <HeaderSlotProvider slot={headerSlot}>
    <HideHeaderProvider onChange={setHeaderHidden}>
    <NavDrawerProvider value={{ open: drawerOpen, setOpen: setDrawerOpen }}>
    <div className="flex h-full">
      {/* Feste Seitenleiste ab lg – Desktop zuerst. */}
      <aside
        className={cn(
          // Die Navigation ist der *getönte* Rand des Blattes, der Inhalt das
          // Papier. Der Tonsprung allein trennt sie – eine Linie obendrein
          // wäre ein Rahmen um die Navigation, und ein Rahmen sagt
          // „Formular“.
          'hidden shrink-0 flex-col bg-sunken lg:flex',
          'transition-[width] duration-200 ease-out',
          // 76px eingeklappt: das Maß aus 2d. Darin liegen 44px-Kacheln mit
          // Luft, und die Symbole stehen mittig statt gequetscht.
          collapsed ? 'w-19' : 'w-60',
        )}
      >
        <SidebarBrand collapsed={collapsed} />
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
          <SidebarNav role={user.role} collapsed={collapsed} />
        </div>
        <SidebarStats collapsed={collapsed} />
        <div className={cn('border-t border-line p-2', collapsed && 'flex justify-center')}>
          <Button
            variant="ghost"
            size={collapsed ? 'iconSm' : 'sm'}
            className={collapsed ? '' : 'w-full justify-start'}
            onClick={() => setCollapsed((value) => !value)}
            aria-label={collapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'}
          >
            {collapsed ? <ChevronsRight aria-hidden /> : <ChevronsLeft aria-hidden />}
            {collapsed ? null : 'Einklappen'}
          </Button>
        </div>
      </aside>

      {/* Schublade unter lg */}
      <DialogPrimitive.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fb-overlay fixed inset-0 z-50 bg-black/45 lg:hidden" />
          <DialogPrimitive.Content
            className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col rounded-r-3xl bg-surface shadow-float lg:hidden"
            aria-label="Navigation"
          >
            <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
            <div className="flex items-center justify-between border-b border-line pr-2">
              <div className="flex-1">
                <SidebarBrand collapsed={false} />
              </div>
              <DialogPrimitive.Close asChild>
                <Button variant="ghost" size="iconSm" aria-label="Navigation schließen">
                  <X aria-hidden />
                </Button>
              </DialogPrimitive.Close>
            </div>
            <div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
              <SidebarNav role={user.role} collapsed={false} onNavigate={() => setDrawerOpen(false)} />
            </div>
            {/* Das Konto in der Schublade: seit die Kopfzeile schmal weg ist,
                ist dies neben der Startseite der einzige Weg dorthin – und der
                einzige, den es von jeder Seite aus gibt. */}
            <div className="flex items-center justify-between gap-2 border-t border-line p-3">
              <UserMenu user={user} />
              <ThemeToggle />
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      {/* Inhalt */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* `relative z-40` ist hier nicht Kosmetik: `backdrop-blur` erzeugt
            einen eigenen Stapelkontext. Ohne eigene Ebene malt die Kopfzeile
            im normalen Fluss – und der Seiteninhalt danach *darüber*. Die
            Trefferliste der Suche lag deshalb hinter Tabellenköpfen und
            Filterfeldern, obwohl sie `z-50` trägt: dieses z-50 gilt nur
            innerhalb der Kopfzeile. */}
        {/* Jetzt Papier statt eigener Streifen: die Seitenleiste ist die
            getönte Fläche, alles rechts davon ist ein Blatt – und ein Blatt
            fängt nicht mit einem Balken an. Der Weichzeichner hält die
            Kopfzeile beim Scrollen lesbar, ohne dass eine Linie sie vom
            Inhalt abschneidet. */}
        {/* Drei Dinge, wie im Entwurf: Suche, die Hauptaktion dieser Seite,
            das Konto. Farbschema und Einstellungen sind ins Kontomenü
            gewandert – als Symbole daneben waren sie drei Klicks, die man im
            Monat einmal braucht, auf Dauerpräsenz. */}
        {/*
          Nur ab `lg` eine Kopfzeile.

          Auf dem Telefon war sie ein Streifen für zwei Symbole – Lupe und
          Konto – über jeder Seite, und der Titel darin sagte dasselbe wie der
          Tab, von dem man gerade kam. Die 56px gehören dem Inhalt. Was sie
          trug, hat jetzt seinen Ort: die Suche steht groß auf der Startseite
          (und nur dort), das Konto neben der Begrüßung und in der
          „Mehr“-Schublade, der Name der Seite in ihrer eigenen Überschrift
          (siehe `PageHeader`).
        */}
        {wide ? (
        <header
          className={cn(
            'relative z-40 flex h-18 shrink-0 items-center gap-3 bg-canvas/80 px-5 backdrop-blur-md',
            // Die Bildseite bringt ihr eigenes Kopfband mit (siehe
            // `useHideAppHeader`).
            headerHidden && 'hidden',
          )}
        >
          <div className="min-w-0 flex-1 lg:max-w-[520px]">
            <GlobalSearch />
          </div>

          {/* Hier hinein rendern die Seiten ihre Hauptaktion (siehe
              `HeaderActions`). Leer bleibt der Platz unsichtbar. */}
          <div ref={headerSlotRef} className="ml-auto flex items-center gap-2" />

          <UserMenu user={user} />
        </header>
        ) : null}

        {/* `overflow-y-auto` allein macht die Waagerechte automatisch zu
            `auto` mit: ragt irgendwo ein Element auch nur drei Pixel heraus,
            lässt sich die *ganze* Seite seitlich verschieben – auf dem Handy
            passiert das beim Wischen sofort und versehentlich. Deshalb sind
            beide Achsen ausdrücklich gesetzt. Was zu breit ist, scrollt in
            seinem eigenen Kasten (Tabellen, Umschalter, Stammbaum). */}
        <main
          id="page-scroll"
          className={cn(
            /*
              `relative` ist hier kein Beiwerk, sondern hält den Bereich dicht.

              Ein absolut positioniertes Kind ohne positionierten Vorfahren
              hängt sonst am Dokument statt am Scrollbereich – es wird von
              dessen `overflow` *nicht* beschnitten und zählt zur scrollbaren
              Höhe der ganzen Seite. Genau das tat die unsichtbare
              Beschriftung „Einträge pro Seite“ ganz unten in der Blätterzeile
              (`sr-only` ist `position: absolute`): das Dokument war dadurch
              1583px hoch bei 963px Fensterhöhe, ließ sich zusätzlich zum
              Inhalt scrollen – Leiste und Seitenleiste wanderten mit – und
              unten stand ein Streifen leerer Fläche.

              Mit `relative` ist der Scrollbereich der Bezugsrahmen für alles
              darin. Das gilt dann für jedes künftige absolut positionierte
              Kind mit, nicht nur für dieses eine.
            */
            'fb-mesh scroll-thin relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto',
            // Unter der Leiste läge sonst die letzte Zeile jeder Seite. Höhe
            // der Leiste (4.5rem) plus ihr Sockel unten – dieselbe Rechnung
            // wie in `mobile-tabs.tsx`, damit beides zusammen bleibt.
            'pb-[calc(4.5rem+max(0.875rem,env(safe-area-inset-bottom))+0.5rem)] lg:pb-0',
          )}
        >
          <Outlet />
        </main>
      </div>

      <MobileTabs />
    </div>
    </NavDrawerProvider>
    </HideHeaderProvider>
    </HeaderSlotProvider>
  );
}
